import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Menu, 
  X,
  ChevronRight,
  Shield,
  Clock,
  Award,
  Users,
  CheckCircle2,
  Star,
  Hammer,
  Building,
  Home,
  Wrench,
  MessageSquare,
  ChevronDown,
  MessageCircle,
  ExternalLink,
  Sparkles,
  // Industry-specific icons
  Flower2,
  Heart,
  Gift,
  Car,
  Zap,
  Droplets,
  PaintBucket,
  SprayCan,
  Scissors,
  UtensilsCrossed,
  Coffee,
  Truck,
  Stethoscope,
  TreePine,
  Laptop,
  Briefcase,
  Scale,
  Calculator,
  HardHat,
  Package
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import SiteWidget from "@/components/SiteWidget";
import { API_URL } from "../utils/api";

// ============================================
// INDUSTRY-BASED ICON SYSTEM
// Maps industries to appropriate icons
// ============================================
const INDUSTRY_ICONS = {
  // Florist / Flowers
  florist: [Flower2, Heart, Gift, Sparkles, Star],
  blomsterhandel: [Flower2, Heart, Gift, Sparkles, Star],
  blommor: [Flower2, Heart, Gift, Sparkles, Star],
  
  // Automotive
  bil: [Car, Wrench, Shield, Clock, CheckCircle2],
  bilverkstad: [Car, Wrench, Shield, Clock, CheckCircle2],
  automotive: [Car, Wrench, Shield, Clock, CheckCircle2],
  
  // Electrical
  elektriker: [Zap, Shield, Home, Clock, CheckCircle2],
  el: [Zap, Shield, Home, Clock, CheckCircle2],
  
  // Plumbing / VVS
  vvs: [Droplets, Wrench, Home, Shield, Clock],
  rörmokare: [Droplets, Wrench, Home, Shield, Clock],
  
  // Painting
  målare: [PaintBucket, Home, Shield, Clock, Star],
  måleri: [PaintBucket, Home, Shield, Clock, Star],
  
  // Cleaning
  städ: [SprayCan, Home, Shield, Clock, Star],
  städning: [SprayCan, Home, Shield, Clock, Star],
  
  // Hair / Beauty
  frisör: [Scissors, Star, Heart, Sparkles, Users],
  salong: [Scissors, Star, Heart, Sparkles, Users],
  
  // Food / Restaurant
  restaurang: [UtensilsCrossed, Star, Heart, Clock, Users],
  café: [Coffee, Star, Heart, Clock, Users],
  kafé: [Coffee, Star, Heart, Clock, Users],
  bageri: [UtensilsCrossed, Star, Heart, Gift, Clock],
  
  // Moving
  flytt: [Truck, Package, Shield, Clock, CheckCircle2],
  flyttfirma: [Truck, Package, Shield, Clock, CheckCircle2],
  
  // Veterinary
  veterinär: [Stethoscope, Heart, Shield, Clock, Star],
  djur: [Stethoscope, Heart, Shield, Clock, Star],
  
  // Landscaping / Garden
  trädgård: [TreePine, Home, Star, Clock, Shield],
  
  // IT / Tech
  it: [Laptop, Shield, Clock, Star, CheckCircle2],
  tech: [Laptop, Shield, Clock, Star, CheckCircle2],
  
  // Consulting / Business
  konsult: [Briefcase, Star, Users, Clock, CheckCircle2],
  advokat: [Scale, Shield, Users, Clock, Star],
  redovisning: [Calculator, Shield, Star, Clock, CheckCircle2],
  
  // Construction
  bygg: [HardHat, Hammer, Shield, Home, Clock],
  snickare: [Hammer, Home, Shield, Clock, Star],
  
  // Health / Wellness
  fysioterapi: [Heart, Users, Shield, Star, Clock],
  hälsa: [Heart, Users, Shield, Star, Clock],
  
  // Default fallback
  default: [Star, Shield, Clock, CheckCircle2, Users]
};

// Get icons for an industry
function getIndustryIcons(industry) {
  if (!industry) return INDUSTRY_ICONS.default;
  
  const industryLower = industry.toLowerCase();
  
  // Direct match
  if (INDUSTRY_ICONS[industryLower]) {
    return INDUSTRY_ICONS[industryLower];
  }
  
  // Partial match
  for (const [key, icons] of Object.entries(INDUSTRY_ICONS)) {
    if (industryLower.includes(key) || key.includes(industryLower)) {
      return icons;
    }
  }
  
  return INDUSTRY_ICONS.default;
}

// ============================================
// FLOATING CTA — sticky mobile button after scroll
// ============================================
function FloatingCTA({ colorPalette, ctaText, scrollToSection }) {
  const [visible, setVisible] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > window.innerHeight * 0.8);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  if (!visible) return null;
  
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.6) 30%)' }}>
      <button
        onClick={() => scrollToSection("kontakt")}
        className="w-full text-white py-3.5 rounded-xl font-semibold text-base shadow-xl flex items-center justify-center gap-2"
        style={{ backgroundColor: colorPalette.accent || '#e5a83b' }}
      >
        {ctaText || "Begär offert idag"}
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}

// ============================================
// STAGGERED CARDS — cards animate in one by one
// ============================================
function StaggerCards({ children, className = "" }) {
  const ref = useRef(null);
  
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const cards = node.querySelectorAll('.stagger-card');
          cards.forEach((card, i) => {
            setTimeout(() => card.classList.add('visible'), i * 120);
          });
          observer.unobserve(node);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  
  return <div ref={ref} className={className}>{children}</div>;
}

// ============================================
// FADE-IN ON SCROLL COMPONENT
// ============================================
function FadeSection({ children }) {
  const ref = useRef(null);
  
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          node.classList.add("visible");
          observer.unobserve(node);
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  
  return <div ref={ref} className="fade-section">{children}</div>;
}

// ============================================
// HERO SECTION – Ken Burns + Parallax
// ============================================
function HeroSection({ images, demo, content, colorPalette, socialProof, trustBadges, scrollToSection }) {
  const sectionRef = useRef(null);
  const overlayRef = useRef(null);
  
  // Parallax: move the overlay layer, NOT the image (so Ken Burns CSS animation is unaffected)
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        if (overlayRef.current && sectionRef.current) {
          const rect = sectionRef.current.getBoundingClientRect();
          if (rect.bottom > 0) {
            const offset = window.scrollY * 0.25;
            overlayRef.current.style.transform = `translateY(${offset}px)`;
          }
        }
        ticking = false;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  return (
    <section ref={sectionRef} className="relative min-h-[85vh] flex items-center overflow-hidden">
      {images?.hero_image ? (
        <>
          <img
            src={images.hero_image}
            alt={demo.company_name}
            className="absolute inset-0 w-full h-full object-cover hero-kenburns"
          />
          <div ref={overlayRef} className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30 will-change-transform" />
        </>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900" />
      )}
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 text-white py-20">
        <div className="max-w-2xl">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            {content.hero_headline || demo.company_name}
          </h1>
          <p className="text-xl md:text-2xl text-white/80 mb-8 leading-relaxed">
            {content.hero_subheadline || `Professionella tjänster i ${demo.city}`}
          </p>
          
          {socialProof && (
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-8 text-sm">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <span className="text-white/90">{socialProof}</span>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 mb-12">
            <button
              onClick={() => scrollToSection("kontakt")}
              className="text-white px-8 py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 transition-all hover:scale-105 shadow-lg"
              style={{ backgroundColor: colorPalette.accent || '#e5a83b' }}
            >
              {content.cta_text || "Begär offert idag"}
              <ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => scrollToSection("tjanster")}
              className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-xl font-semibold text-lg backdrop-blur-sm transition-all border border-white/20"
            >
              Våra tjänster
            </button>
          </div>

          <div className="flex flex-wrap gap-6 text-white/80 text-sm">
            {trustBadges.length > 0 ? (
              trustBadges.slice(0, 3).map((badge, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <span>{badge}</span>
                </div>
              ))
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <span>Kostnadsfri offert</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <span>Snabb återkoppling</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}


export default function AIDemoPage() {
  const { slug } = useParams();
  const [demo, setDemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    loadDemo();
  }, [slug]);

  // Update browser tab title when demo loads
  useEffect(() => {
    if (demo?.company_name) {
      document.title = `${demo.company_name} | Demo`;
    }
    return () => {
      document.title = "Elido Demo";
    };
  }, [demo]);

  const loadDemo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/demos/by-slug/${slug}`);
      if (!response.ok) {
        throw new Error("Demo not found");
      }
      const data = await response.json();
      if (data.success) {
        setDemo(data.demo);
      } else {
        throw new Error("Demo not found");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        {/* SEO handled by GlobalSEOHead */}
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Laddar demo...</p>
        </div>
      </div>
    );
  }

  if (error || !demo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        {/* SEO handled by GlobalSEOHead */}
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Building className="w-8 h-8 text-gray-400" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Demo hittades inte</h1>
          <p className="text-gray-500 mb-6">Den begärda demon finns inte eller har tagits bort.</p>
          <a href="/" className="text-blue-600 hover:underline">Gå till startsidan →</a>
        </div>
      </div>
    );
  }

  const content = demo.generated_content || {};
  const services = content.services || [];
  const faq = content.faq || [];
  const images = demo.generated_images || {};
  const trustBadges = content.trust_badges || [];
  const socialProof = content.social_proof || "";
  
  // Dynamic color palette - use more subtle application
  const colorPalette = content.color_palette || {
    primary: "#1e3a4f",
    secondary: "#2d4a5e",
    accent: "#3d8a98"
  };

  return (
    <div className="min-h-screen bg-white font-['Inter']">

      {/* Footer */}
      {/* SEO: canonical and robots handled by GlobalSEOHead */}
      <Helmet>
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      
      <Toaster position="top-center" richColors />
      
      {/* Clean, minimal CSS - accent color only for buttons and small details */}
      <style>{`
        /* CTA buttons - primary color */
        .demo-cta-primary {
          background: ${colorPalette.primary};
          transition: all 0.2s ease;
        }
        .demo-cta-primary:hover {
          background: ${colorPalette.secondary};
        }
        
        /* AI demo gradient - for nav logo and footer */
        .ai-demo-gradient {
          background: linear-gradient(135deg, ${colorPalette.primary} 0%, ${colorPalette.secondary} 100%);
        }
        
        /* AI demo CTA - prominent call-to-action buttons */
        .ai-demo-cta {
          background: ${colorPalette.primary};
          transition: all 0.2s ease;
        }
        .ai-demo-cta:hover {
          background: ${colorPalette.secondary};
          transform: translateY(-1px);
        }
        
        /* Text accents */
        .demo-accent-text {
          color: ${colorPalette.primary};
        }
        .demo-accent-border {
          border-color: ${colorPalette.primary};
        }
        
        /* Icon backgrounds - ALWAYS high contrast for visibility */
        .demo-icon-bg {
          background: ${colorPalette.primary};
        }
        
        /* Hero entrance — clip-path reveal + scale on mobile, Ken Burns on desktop */
        @keyframes heroReveal {
          0% { clip-path: inset(8% 8% 8% 8%); transform: scale(1.12); filter: brightness(0.6); }
          100% { clip-path: inset(0% 0% 0% 0%); transform: scale(1); filter: brightness(1); }
        }
        @keyframes kenburnsDesktop {
          0% { transform: scale(1.02); }
          100% { transform: scale(1.12); }
        }
        .hero-kenburns {
          animation: heroReveal 2.5s cubic-bezier(0.22,1,0.36,1) forwards;
        }
        @media (min-width: 768px) {
          .hero-kenburns {
            animation: kenburnsDesktop 20s ease-out forwards;
          }
        }
        
        /* Staggered card entrance */
        @keyframes cardSlideUp {
          0% { opacity: 0; transform: translateY(24px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .stagger-card {
          opacity: 0;
        }
        .stagger-card.visible {
          animation: cardSlideUp 0.6s cubic-bezier(0.22,1,0.36,1) forwards;
        }
        
        /* Fade-in on scroll */
        .fade-section {
          opacity: 0;
          transform: translateY(32px);
          transition: opacity 0.8s cubic-bezier(0.22,1,0.36,1), transform 0.8s cubic-bezier(0.22,1,0.36,1);
        }
        .fade-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        /* Gradient overlay on images */
        .img-gradient-overlay {
          position: relative;
        }
        .img-gradient-overlay::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: linear-gradient(180deg, transparent 60%, ${colorPalette.primary}18 100%);
          pointer-events: none;
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${colorPalette.primary || '#1e3a4f'} 0%, ${colorPalette.secondary || '#2d4a5e'} 100%)` }}
              >
                <Building className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-gray-900 text-lg">
                {demo.company_name}
              </span>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection("tjanster")} className="text-gray-600 hover:text-gray-900 text-sm font-medium">
                Tjänster
              </button>
              <button onClick={() => scrollToSection("om-oss")} className="text-gray-600 hover:text-gray-900 text-sm font-medium">
                Om oss
              </button>
              <button onClick={() => scrollToSection("faq")} className="text-gray-600 hover:text-gray-900 text-sm font-medium">
                FAQ
              </button>
              <button 
                onClick={() => scrollToSection("kontakt")}
                className="text-white px-5 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-90"
                style={{ backgroundColor: colorPalette.primary || '#1e3a4f' }}
              >
                {content.cta_text || "Kontakta oss"}
              </button>
            </div>

            {/* Mobile menu button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-600"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-3">
            <button onClick={() => scrollToSection("tjanster")} className="block w-full text-left py-2 text-gray-600">Tjänster</button>
            <button onClick={() => scrollToSection("om-oss")} className="block w-full text-left py-2 text-gray-600">Om oss</button>
            <button onClick={() => scrollToSection("faq")} className="block w-full text-left py-2 text-gray-600">FAQ</button>
            <button 
              onClick={() => scrollToSection("kontakt")} 
              className="block w-full text-white py-3 rounded-lg text-center font-medium"
              style={{ backgroundColor: colorPalette.primary || '#1e3a4f' }}
            >
              {content.cta_text || "Kontakta oss"}
            </button>
          </div>
        )}
      </nav>

      {/* Hero Section - Large, impactful design with Ken Burns + Parallax */}
      <HeroSection
        images={images}
        demo={demo}
        content={content}
        colorPalette={colorPalette}
        socialProof={socialProof}
        trustBadges={trustBadges}
        scrollToSection={scrollToSection}
      />

      {/* Services Section - With colored background */}
      <FadeSection>
      <section id="tjanster" className="py-16" style={{ backgroundColor: colorPalette.primary || '#1e3a4f' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-12 text-center">
            <p className="text-sm font-medium uppercase tracking-wide mb-2" style={{ color: colorPalette.accent || '#e5a83b' }}>Tjänster</p>
            <h2 className="text-2xl md:text-3xl font-semibold text-white">
              Vad vi hjälper dig med
            </h2>
          </div>

          <StaggerCards className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => {
              const industryIcons = getIndustryIcons(demo.industry);
              const ServiceIcon = industryIcons[i % industryIcons.length];
              
              return (
                <div 
                  key={i} 
                  className="stagger-card bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/15 transition-colors"
                >
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                    style={{ backgroundColor: colorPalette.accent || '#e5a83b' }}
                  >
                    <ServiceIcon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">{service.title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </StaggerCards>
        </div>
      </section>
      </FadeSection>

      {/* About Section */}
      <FadeSection>
      <section id="om-oss" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p 
                className="text-sm font-semibold uppercase tracking-wide mb-3"
                style={{ color: colorPalette.accent || '#e5a83b' }}
              >
                Om {demo.company_name}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Din lokala partner i {demo.city}
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                {content.about_section || `${demo.company_name} erbjuder professionella tjänster med fokus på kvalitet och kundnöjdhet.`}
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Shield, text: "Försäkrad" },
                  { icon: Clock, text: "Snabb service" },
                  { icon: Award, text: "Kvalitetsgaranti" },
                  { icon: Users, text: "Personlig kontakt" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${colorPalette.accent || '#e5a83b'}20` }}
                    >
                      <item.icon className="w-5 h-5" style={{ color: colorPalette.accent || '#e5a83b' }} />
                    </div>
                    <span className="text-gray-700 font-medium text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative img-gradient-overlay">
              {images?.hero_image || (images?.service_images && images.service_images[0]) ? (
                <img 
                  src={images.service_images?.[0] || images.hero_image}
                  alt={demo.company_name}
                  className="w-full h-[400px] object-cover rounded-2xl shadow-lg"
                />
              ) : (
                <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center">
                  <Building className="w-24 h-24 text-gray-400" />
                </div>
              )}
              <div 
                className="absolute -bottom-4 -left-4 text-white p-5 rounded-xl shadow-lg"
                style={{ backgroundColor: colorPalette.primary || '#1e3a4f' }}
              >
                <div className="flex gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" style={{ color: colorPalette.accent || '#e5a83b' }} />
                  ))}
                </div>
                <p className="text-sm font-medium">Nöjda kunder</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      </FadeSection>

      {/* Why Choose Us - Color Block Section with AI image */}
      <FadeSection>
      <section className="py-16" style={{ backgroundColor: colorPalette.primary || '#1e3a4f' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p 
              className="text-sm font-medium uppercase tracking-wide mb-2"
              style={{ color: colorPalette.accent || '#e5a83b' }}
            >
              Varför välja oss
            </p>
            <h2 className="text-2xl md:text-3xl font-semibold text-white">
              Det som gör oss annorlunda
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "Erfarna proffs", text: "Lång erfarenhet och expertis inom branschen" },
              { icon: Clock, title: "Kostnadsfri offert", text: "Vi lämnar alltid en tydlig offert utan förpliktelser" },
              { icon: Award, title: "Kvalitetsgaranti", text: "Vi står för kvalitet i allt vi gör" },
              { icon: MapPin, title: "Lokal närvaro", text: `Vi finns nära dig och känner till lokala förhållanden` }
            ].map((item, i) => (
              <div 
                key={i} 
                className="bg-white/5 rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-colors"
              >
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${colorPalette.accent || '#e5a83b'}30` }}
                >
                  <item.icon className="w-6 h-6" style={{ color: colorPalette.accent || '#e5a83b' }} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
          
          {/* AI-generated contextual image — below the cards */}
          {images?.why_us_image && (
            <div className="mt-10 img-gradient-overlay rounded-2xl overflow-hidden">
              <img
                src={images.why_us_image}
                alt={`${demo.company_name} – ${demo.industry} i ${demo.city}`}
                className="w-full h-[280px] md:h-[440px] lg:h-[500px] object-cover"
              />
            </div>
          )}
        </div>
      </section>
      </FadeSection>

      {/* FAQ Section */}
      {faq.length > 0 && (
        <FadeSection>
        <section id="faq" className="py-20 bg-gray-50">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-12">
              <p className="text-sm font-semibold text-[#e94560] uppercase tracking-wide mb-3">Vanliga frågor</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Har du frågor?
              </h2>
            </div>

            <div className="space-y-4">
              {faq.map((item, i) => (
                <div 
                  key={i}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <span className="font-semibold text-gray-900 pr-4">{item.question}</span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-6 text-gray-600">
                      {item.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
        </FadeSection>
      )}

      {/* Contact Section */}
      <FadeSection>
      <section id="kontakt" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <p 
                className="text-sm font-semibold uppercase tracking-wide mb-3"
                style={{ color: colorPalette.accent || '#e5a83b' }}
              >
                Kontakta {demo.company_name}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Få en kostnadsfri offert
              </h2>
              <p className="text-gray-600 text-lg mb-10">
                Fyll i formuläret så återkommer vi inom 24 timmar.
              </p>

              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: colorPalette.primary || '#1e3a4f' }}
                  >
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Telefon</p>
                    <span className="text-lg font-semibold text-gray-900">010-123 45 67</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: colorPalette.primary || '#1e3a4f' }}
                  >
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">E-post</p>
                    <span className="text-lg font-semibold text-gray-900">info@{demo.slug.replace(/-ab$/, '').replace(/-ab-/g, '-')}.se</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: colorPalette.primary || '#1e3a4f' }}
                  >
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Plats</p>
                    <span className="text-lg font-semibold text-gray-900">{demo.city}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Namn</label>
                  <input
                    type="text"
                    placeholder="Ditt namn"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e94560]/20 focus:border-[#e94560]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">E-post</label>
                  <input
                    type="email"
                    placeholder="din@email.se"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e94560]/20 focus:border-[#e94560]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
                  <input
                    type="tel"
                    placeholder="07X-XXX XX XX"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e94560]/20 focus:border-[#e94560]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Meddelande</label>
                  <textarea
                    rows={4}
                    placeholder="Beskriv ditt projekt..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e94560]/20 focus:border-[#e94560] resize-none"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Genom att fortsätta godkänner du vår{" "}
                  <a href="/integritetspolicy" className="text-[#255E69] hover:underline" target="_blank" rel="noopener noreferrer">
                    integritetspolicy
                  </a>.
                </p>
                <button
                  type="submit"
                  className="w-full text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all hover:opacity-90"
                  style={{ backgroundColor: colorPalette.primary || '#1e3a4f' }}
                >
                  {content.cta_secondary || content.cta_text || "Skicka förfrågan"}
                  <ChevronRight className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
      </FadeSection>
      <footer 
        className="text-white py-12"
        style={{ background: `linear-gradient(135deg, ${colorPalette.primary || '#1e3a4f'} 0%, ${colorPalette.secondary || '#2d4a5e'} 100%)` }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <Building className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl">{demo.company_name}</span>
            </div>
            
            <p className="text-white/50 text-sm text-center">
              Demo framtagen av <a href="https://elido.nu" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white">Elido AB</a>
            </p>
          </div>
          
          <div className="border-t border-white/10 mt-8 pt-8 text-center">
            <p className="text-white/30 text-sm">
              © {new Date().getFullYear()} {demo.company_name}. Demo.
            </p>
          </div>
          
          {/* Elido Trust Section */}
          <div className="border-t border-white/10 mt-8 pt-8">
            <div className="bg-white/5 rounded-xl p-6 text-center">
              <p className="text-white/80 text-sm mb-3">
                Denna demo är skapad av <strong>Elido AB</strong> – vi hjälper företag få fler kunder genom smarta hemsidor.
              </p>
              <a 
                href="https://elido.nu/om-oss" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm font-medium transition-colors"
              >
                Läs mer om oss
                <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Intentionally NO Elido SiteWidget here — /d/:slug renders the
          customer's generated demo and must not show Elido branding/chatbot. */}
      
      {/* Floating CTA on mobile — appears after scrolling past hero */}
      <FloatingCTA colorPalette={colorPalette} ctaText={content.cta_text} scrollToSection={scrollToSection} />
    </div>
  );
}
